import React from "react";
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";

interface ExportPaginationProps {
  format: string;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  sortOrder: 'asc' | 'desc';
  dataMode: 'full' | 'paginated' | null;
  itandiLoading: boolean;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSortChange: (newOrder: 'asc' | 'desc') => void;
}

export const ExportPagination: React.FC<ExportPaginationProps> = ({
  format,
  page,
  rowsPerPage,
  totalCount,
  sortOrder,
  dataMode,
  itandiLoading,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}) => {
  return (
    <Box sx={{ ml: 2.5, mr: 2.5, mt: 2, mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* 上段: ソート切り替え、表示件数セレクター */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
        {format === 'itandi' && (
          <ToggleButtonGroup
            value={sortOrder}
            exclusive
            onChange={(event, newOrder) => {
              if (newOrder !== null && !itandiLoading) {
                onSortChange(newOrder);
              }
            }}
            size="small"
            sx={{ height: '40px' }}
          >
            <ToggleButton
              value="asc"
              sx={{
                minWidth: '70px',
                '&.Mui-selected': {
                  backgroundColor: 'rgb(80, 212, 170)',
                  color: 'white',
                  fontWeight: 'bold',
                  '&:hover': { backgroundColor: 'rgb(50, 225, 170)' },
                },
              }}
            >
              登録順
            </ToggleButton>
            <ToggleButton
              value="desc"
              sx={{
                minWidth: '70px',
                '&.Mui-selected': {
                  backgroundColor: 'rgb(80, 212, 170)',
                  color: 'white',
                  fontWeight: 'bold',
                  '&:hover': { backgroundColor: 'rgb(50, 225, 170)' },
                },
              }}
            >
              新着順
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        <FormControl
          size="small"
          sx={{
            minWidth: 120,
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: 'rgb(80, 212, 170)' },
              '&:hover fieldset': { borderColor: 'rgb(50, 225, 170)' },
              '&.Mui-focused fieldset': { borderColor: 'rgb(50, 225, 170)' },
            },
            '& .MuiInputLabel-root': {
              color: 'rgb(80, 212, 170)',
              '&.Mui-focused': { color: 'rgb(50, 225, 170)' },
            },
          }}
        >
          <InputLabel sx={{ color: 'rgb(80, 212, 170)' }}>表示件数</InputLabel>
          <Select
            native
            value={rowsPerPage}
            onChange={(e) => onRowsPerPageChange(e as React.ChangeEvent<HTMLInputElement>)}
            label="表示件数"
            sx={{ fontSize: '0.875rem' }}
          >
            <option value={5}>5件</option>
            <option value={10}>10件</option>
            <option value={50}>50件</option>
            <option value={100}>100件</option>
          </Select>
        </FormControl>
      </Box>

      {/* 下段: ページ送りボタンと表示情報 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box sx={{ minWidth: 120 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            onClick={(e) => onPageChange(e, 0)}
            disabled={page === 0}
            sx={{ minWidth: '40px', fontSize: '1.2rem' }}
          >
            ◀◀
          </Button>
          <Button
            size="small"
            onClick={(e) => onPageChange(e, page - 1)}
            disabled={page === 0}
            sx={{ minWidth: '40px', fontSize: '1.2rem' }}
          >
            ◀
          </Button>
          <Typography variant="body2" sx={{ px: 2, minWidth: 100, textAlign: 'center' }}>
            {page + 1} / {Math.ceil(totalCount / rowsPerPage)}
          </Typography>
          <Button
            size="small"
            onClick={(e) => onPageChange(e, page + 1)}
            disabled={page >= Math.ceil(totalCount / rowsPerPage) - 1}
            sx={{ minWidth: '40px', fontSize: '1.2rem' }}
          >
            ▶
          </Button>
          <Button
            size="small"
            onClick={(e) => onPageChange(e, Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1))}
            disabled={dataMode === 'paginated' || page >= Math.ceil(totalCount / rowsPerPage) - 1}
            sx={{ minWidth: '40px', fontSize: '1.2rem' }}
          >
            ▶▶
          </Button>
        </Box>
        <Typography variant="body2" sx={{ minWidth: 120, textAlign: 'right' }}>
          {`${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalCount)}件目 / 全${totalCount}件`}
        </Typography>
      </Box>
    </Box>
  );
};
